// Path: frontend/src/lib/videoManager.ts
// Author: Vibe Coding System (Luna & Neo)
// Description: AI Video Generation API & Local State Recovery Manager (Ghost in the Shell)

import { api } from './api'; // 引入已有的 axios 实例

// ==========================================
// 🎬 1. Type Definitions (视频生成接口类型)
// ==========================================

export type VideoErrorCode = 
  | 'API_ERROR'
  | 'SENSITIVE_CONTENT'
  | 'TOKEN_INSUFFICIENT'
  | 'EXTRACTION_FAILED'
  | 'INTERNAL_ERROR'
  | 'NETWORK_ERROR'
  | 'UNKNOWN_ERROR';

export interface VideoGenerationRequest {
  slide_id: number;
  user_input: string;
  reference_image_urls?: string[];
  model_override?: string;
}

export interface VideoGenerationResponse {
  type: 'success' | 'error' | 'pending';
  status?: string;
  content: string;
  video_url?: string;
  original_video_url?: string;
  output_money?: number;
  user_msg_id?: number;
  ai_msg_id?: number;
  error_code?: VideoErrorCode;
  error_message?: string;
}

export interface VideoErrorResult {
  success: false;
  errorCode: VideoErrorCode;
  errorMessage: string;
  userMsgId?: number;
}

export interface VideoSuccessResult {
  success: true;
  videoUrl: string;
  userMsgId: number;
  aiMsgId: number;
  outputMoney: number;
}

export type VideoResult = VideoSuccessResult | VideoErrorResult | { success: true; pending: true; userMsgId: number };

export interface VideoHistoryRecord {
  user_msg_id: number;
  ai_msg_id: number | null;
  prompt: string;
  reference_images: string[];
  video_url: string;
  created_at: string;
  output_money: number;
  status: 'generating' | 'success' | 'error';
  error_code?: VideoErrorCode;
  error_message?: string;
}

export interface DeleteVideoHistoryResponse {
  status: string;
  deleted_user_msg_id: number;
  deleted_ai_msg_id: number | null;
}

// ==========================================
// 🚀 2. API Callers (基础接口调用)
// ==========================================

/**
 * 提交 AI 视频生成任务 (异步，瞬间返回 pending)
 */
const VIDEO_API_TIMEOUT = 300000;
const VIDEO_POLL_INTERVAL = 30000;

export const generateVideoAPI = (data: VideoGenerationRequest) => 
  api.post<VideoGenerationResponse>('/slides/chat/video', data, { timeout: VIDEO_API_TIMEOUT });

/**
 * 获取幻灯片的视频生成历史 (用于展示和轮询)
 */
export const getSlideVideoHistoryAPI = (slideId: number) => 
  api.get<VideoHistoryRecord[]>(`/slides/${slideId}/video-history`);

/**
 * 删除某条视频生成历史
 */
export const deleteVideoHistoryAPI = (userMsgId: number) => 
  api.delete<DeleteVideoHistoryResponse>(`/slides/video-history/${userMsgId}`);


// ==========================================
// ⚠️ 3. Error Handling (错误处理工具)
// ==========================================

export const VIDEO_ERROR_MESSAGES: Record<VideoErrorCode, string> = {
  SENSITIVE_CONTENT: '内容包含敏感信息，请修改提示词后重试',
  TOKEN_INSUFFICIENT: 'Token余额不足，请联系老师充值',
  API_ERROR: '生成失败，请稍后重试',
  EXTRACTION_FAILED: '生成失败，请稍后重试',
  INTERNAL_ERROR: '服务器繁忙，请稍后重试',
  NETWORK_ERROR: '网络连接失败，请检查网络后重试',
  UNKNOWN_ERROR: '发生未知错误，请稍后重试',
};

export const getVideoErrorMessage = (errorCode?: VideoErrorCode, fallbackMessage?: string): string => {
  if (errorCode && VIDEO_ERROR_MESSAGES[errorCode]) {
    return VIDEO_ERROR_MESSAGES[errorCode];
  }
  return fallbackMessage || VIDEO_ERROR_MESSAGES.UNKNOWN_ERROR;
};

export const parseVideoError = (error: any): VideoErrorResult => {
  if (error.response?.data) {
    const { error_code, error_message, user_msg_id } = error.response.data;
    
    if (error_code) {
      return {
        success: false,
        errorCode: error_code as VideoErrorCode,
        errorMessage: getVideoErrorMessage(error_code, error_message),
        userMsgId,
      };
    }
    
    if (error.response.data.detail) {
      return {
        success: false,
        errorCode: 'API_ERROR',
        errorMessage: error.response.data.detail,
      };
    }
  }
  
  if (error.message?.includes('Network Error') || error.message?.includes('timeout')) {
    return {
      success: false,
      errorCode: 'NETWORK_ERROR',
      errorMessage: VIDEO_ERROR_MESSAGES.NETWORK_ERROR,
    };
  }
  
  return {
    success: false,
    errorCode: 'UNKNOWN_ERROR',
    errorMessage: error.message || VIDEO_ERROR_MESSAGES.UNKNOWN_ERROR,
  };
};

export const handleVideoResponse = (data: VideoGenerationResponse): VideoResult => {
  if (data.type === 'success' && data.video_url) {
    return {
      success: true,
      videoUrl: data.video_url,
      userMsgId: data.user_msg_id!,
      aiMsgId: data.ai_msg_id!,
      outputMoney: data.output_money || 0,
    };
  }
  
  if (data.type === 'pending' || data.status === 'generating') {
    return {
      success: true,
      pending: true,
      userMsgId: data.user_msg_id!,
    };
  }
  
  if (data.type === 'error') {
    return {
      success: false,
      errorCode: (data.error_code as VideoErrorCode) || 'API_ERROR',
      errorMessage: getVideoErrorMessage(data.error_code, data.error_message || data.content),
      userMsgId: data.user_msg_id,
    };
  }
  
  return {
    success: false,
    errorCode: 'UNKNOWN_ERROR',
    errorMessage: data.content || VIDEO_ERROR_MESSAGES.UNKNOWN_ERROR,
  };
};


// ==========================================
// 👻 4. Ghost in the Shell: Video Recovery Manager
// ==========================================

const PENDING_TASK_KEY = 'vibe_pending_video_tasks';

export interface PendingVideoTask {
  userMsgId: number; // 任务追踪核心 ID
  slideId: number;
  prompt: string;
  startTime: string;
}

export interface PollingCallbacks {
  onVideoReady: (video: VideoHistoryRecord) => void;
  onVideoFailed?: (error: { userMsgId: number; errorCode: VideoErrorCode; errorMessage: string }) => void;
}

export const VideoRecoveryManager = {
  /**
   * 1. 记录幽灵任务：在 API 返回 pending 后，立即存入 LocalStorage
   */
  registerTask(slideId: number, prompt: string, userMsgId: number) {
    const tasks: PendingVideoTask[] = JSON.parse(localStorage.getItem(PENDING_TASK_KEY) || '[]');
    
    if (tasks.some(t => t.userMsgId === userMsgId)) return;

    const newTask: PendingVideoTask = {
      userMsgId,
      slideId,
      prompt,
      startTime: new Date().toISOString(),
    };
    
    tasks.push(newTask);
    localStorage.setItem(PENDING_TASK_KEY, JSON.stringify(tasks));
    console.log(`[Vibe System] 墓碑已刻下，任务托管中: msg_id=${userMsgId}`);
  },

  /**
   * 2. 清除任务：生成成功或失败后抹除 LocalStorage 记录
   */
  resolveTask(userMsgId: number) {
    let tasks: PendingVideoTask[] = JSON.parse(localStorage.getItem(PENDING_TASK_KEY) || '[]');
    tasks = tasks.filter(t => t.userMsgId !== userMsgId);
    localStorage.setItem(PENDING_TASK_KEY, JSON.stringify(tasks));
  },

  /**
   * 3. 死而复生：页面加载 (mounted) 时调用，自动唤醒丢失的任务并开启静默轮询
   * @param callbacks 成功和失败的回调函数
   */
  resurrectTasks(callbacks: PollingCallbacks) {
    const tasks: PendingVideoTask[] = JSON.parse(localStorage.getItem(PENDING_TASK_KEY) || '[]');
    if (tasks.length === 0) return;

    console.log('[Vibe System] 发现遗留的生成任务，启动量子轮询...', tasks);

    tasks.forEach(task => {
      this.startPolling(task.slideId, task.userMsgId, callbacks);
    });
  },

  /**
   * 4. 轮询引擎 (可供当前页面直接调用)
   */
  startPolling(slideId: number, userMsgId: number, callbacks: PollingCallbacks | ((video: VideoHistoryRecord) => void)) {
    const onVideoReady = typeof callbacks === 'function' ? callbacks : callbacks.onVideoReady;
    const onVideoFailed = typeof callbacks === 'function' ? undefined : callbacks.onVideoFailed;
    
    const pollInterval = setInterval(async () => {
      try {
        const { data: historyList } = await getSlideVideoHistoryAPI(slideId);
        
        const targetVideo = historyList.find(h => h.user_msg_id === userMsgId);

        if (!targetVideo) return;

        if (targetVideo.status === 'error') {
          clearInterval(pollInterval);
          this.resolveTask(userMsgId);
          
          const errorCode = targetVideo.error_code || 'API_ERROR';
          const errorMessage = getVideoErrorMessage(targetVideo.error_code, targetVideo.error_message);
          
          console.warn(`[Vibe System] 视频生成失败 (msg_id: ${userMsgId}): ${errorCode} - ${errorMessage}`);
          
          if (onVideoFailed) {
            onVideoFailed({
              userMsgId,
              errorCode,
              errorMessage,
            });
          }
          return;
        }

        if (targetVideo.status === 'success' && targetVideo.video_url && targetVideo.video_url.trim() !== "") {
          clearInterval(pollInterval);
          this.resolveTask(userMsgId);
          
          console.log(`[Vibe System] 捕获到离线生成的视频！URL: ${targetVideo.video_url}`);
          
          onVideoReady(targetVideo);
        }
      } catch (err) {
        console.warn(`[Vibe System] 轮询异常 (msg_id: ${userMsgId})，将继续重试...`, err);
      }
    }, VIDEO_POLL_INTERVAL);
    
    return pollInterval; 
  }
};